// Facility Geocoding — a spreadsheet-style audit of every cached facility
// and the coordinates we have on file. Users can search / sort / filter
// down to suspicious rows and click through to Google Maps in a new tab
// to eyeball whether the pin is where it should be.
//
// Two Google Maps deep-links per row:
//   - Coords → opens Maps centered on the stored lat/lng. Confirms
//     whether the pin makes sense for the facility.
//   - Address → opens Maps searching for the address text as if the
//     user typed it themselves. Confirms whether Google can even find
//     the address today. Comparing the two side-by-side is the fastest
//     way to spot "we geocoded to Kansas but Google puts this address
//     in Ohio."
//
// Once the shared /api/geocode/lookup cache is populated (which won't
// happen automatically — the batch back-fill writes to CachedFacility
// today), a follow-up can pull `locationType` / `partialMatch` here
// and flag suspicious rows with color. For now the "Quality" column
// reads whatever the facility row already carries (Salesforce vs
// Google vs failed) so at least the un-geocoded ones are obvious.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCache } from '../../hooks/useCache';
import './FacilityGeocoding.css';

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

// Show rows in one of these quality buckets. Order defines the sort
// order in the summary chips.
const QUALITY_BUCKETS = [
  { id: 'missing', label: 'Missing coords', matcher: (f) => f.billingLatitude == null || f.billingLongitude == null },
  { id: 'failed',  label: 'Geocode failed', matcher: (f) => f.geocodeSource === 'google-failed' },
  { id: 'google',  label: 'Google geocoded', matcher: (f) => f.geocodeSource === 'google' && f.billingLatitude != null },
  { id: 'salesforce', label: 'From Salesforce', matcher: (f) => (f.geocodeSource === 'salesforce' || !f.geocodeSource) && f.billingLatitude != null },
];

const SORTABLE_COLS = [
  { id: 'name',     label: 'Facility',   accessor: (f) => (f.name || '').toLowerCase() },
  { id: 'address',  label: 'Address',    accessor: (f) => buildAddress(f).toLowerCase() },
  { id: 'state',    label: 'State',      accessor: (f) => (f.billingState || '').toLowerCase() },
  { id: 'lat',      label: 'Lat',        accessor: (f) => f.billingLatitude ?? -1e9 },
  { id: 'lng',      label: 'Lng',        accessor: (f) => f.billingLongitude ?? -1e9 },
  { id: 'source',   label: 'Source',     accessor: (f) => (f.geocodeSource || 'zz').toLowerCase() },
];

function buildAddress(f) {
  return [f.billingStreet, f.billingCity, f.billingState, f.billingPostalCode]
    .filter(Boolean)
    .map(s => String(s).trim())
    .join(', ');
}

// Two link styles used by every row.
function coordsMapUrl(lat, lng) {
  // Zoom 17 shows the individual building; @lat,lng,17z is the well-
  // documented Google Maps URL shape and works without an API key.
  return `https://www.google.com/maps/@${lat},${lng},17z`;
}
function addressSearchUrl(addr) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
}

function bucketFor(f) {
  return QUALITY_BUCKETS.find(b => b.matcher(f))?.id || 'salesforce';
}

function FacilityGeocoding() {
  const cache = useCache();

  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter / search / sort state.
  const [searchTerm, setSearchTerm] = useState('');
  const [activeBucket, setActiveBucket] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Load facility list once — the same shape Facilities page uses.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await cache.getCachedFacilities();
        if (!cancelled && res?.success) {
          setFacilities(res.facilities || []);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load facilities.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // cache is a stable hook result; the useEffect only needs to run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Quality-bucket counts for the chip row.
  const bucketCounts = useMemo(() => {
    const counts = { all: facilities.length };
    for (const b of QUALITY_BUCKETS) counts[b.id] = 0;
    for (const f of facilities) counts[bucketFor(f)] += 1;
    return counts;
  }, [facilities]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const rows = facilities.filter(f => {
      if (activeBucket !== 'all' && bucketFor(f) !== activeBucket) return false;
      if (!q) return true;
      const blob = [
        f.name,
        f.billingStreet,
        f.billingCity,
        f.billingState,
        f.billingPostalCode,
        f.vitalEngine_Facility_ID__c,
        f.id,
      ].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
    const accessor = (SORTABLE_COLS.find(c => c.id === sortField) || SORTABLE_COLS[0]).accessor;
    const dir = sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
    return rows;
  }, [facilities, searchTerm, activeBucket, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [searchTerm, activeBucket, sortField, sortDir, pageSize]);

  const lastPage = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const toggleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir(field === 'lat' || field === 'lng' ? 'desc' : 'asc');
    }
  }, [sortField]);

  const exportCsv = () => {
    const header = ['Facility ID', 'VE Facility ID', 'Name', 'Street', 'City', 'State', 'Zip', 'Country', 'Lat', 'Lng', 'Source', 'GeocodedAt'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = filtered.map(f => [
      f.id,
      f.vitalEngine_Facility_ID__c || '',
      f.name || '',
      f.billingStreet || '',
      f.billingCity || '',
      f.billingState || '',
      f.billingPostalCode || '',
      f.billingCountry || '',
      f.billingLatitude ?? '',
      f.billingLongitude ?? '',
      f.geocodeSource || '',
      f.geocodedAt || '',
    ].map(escape).join(','));
    const csv = [header.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facility-geocoding_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortIndicator = (field) => sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  return (
    <div className="fac-geo">
      <div className="fac-geo-intro">
        <p>
          Every cached facility with its stored latitude / longitude and the
          source of that coordinate. Use the <strong>coords</strong> link to
          confirm the pin is where you'd expect the facility to be, and the{' '}
          <strong>address</strong> link to see what Google Maps thinks about
          the address independently. Anywhere the two don't agree is a
          candidate for cleanup in Salesforce.
        </p>
      </div>

      <div className="fac-geo-controls">
        <input
          type="search"
          className="fac-geo-search"
          placeholder="Search by name, address, city, state, VE ID…"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <div className="fac-geo-chips" role="group" aria-label="Filter by geocode quality">
          <button
            type="button"
            className={`fac-geo-chip ${activeBucket === 'all' ? 'active' : ''}`}
            onClick={() => setActiveBucket('all')}
          >
            All ({bucketCounts.all})
          </button>
          {QUALITY_BUCKETS.map(b => (
            <button
              key={b.id}
              type="button"
              className={`fac-geo-chip fac-geo-chip-${b.id} ${activeBucket === b.id ? 'active' : ''}`}
              onClick={() => setActiveBucket(b.id)}
            >
              {b.label} ({bucketCounts[b.id] || 0})
            </button>
          ))}
        </div>
        <div className="fac-geo-actions">
          <select
            value={pageSize}
            onChange={e => setPageSize(parseInt(e.target.value, 10))}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
          <button
            type="button"
            className="fac-geo-export"
            onClick={exportCsv}
            disabled={filtered.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {loading && <div className="fac-geo-empty">Loading facilities…</div>}
      {error && <div className="fac-geo-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="fac-geo-count">
            Showing <strong>{paged.length.toLocaleString()}</strong> of{' '}
            <strong>{filtered.length.toLocaleString()}</strong> matches (page {page} of {lastPage})
          </div>

          <div className="fac-geo-table-wrap">
            <table className="fac-geo-table">
              <thead>
                <tr>
                  {SORTABLE_COLS.map(c => (
                    <th
                      key={c.id}
                      onClick={() => toggleSort(c.id)}
                      className={`fac-geo-sortable ${c.id === 'lat' || c.id === 'lng' ? 'num' : ''}`}
                    >
                      {c.label}{sortIndicator(c.id)}
                    </th>
                  ))}
                  <th>Verify on Google Maps</th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 && (
                  <tr>
                    <td colSpan={SORTABLE_COLS.length + 1} className="fac-geo-empty-row">
                      No facilities match this filter.
                    </td>
                  </tr>
                )}
                {paged.map(f => {
                  const addr = buildAddress(f);
                  const hasCoords = f.billingLatitude != null && f.billingLongitude != null;
                  const bucket = bucketFor(f);
                  return (
                    <tr key={`${f.id}-${f.vitalEngine_Facility_ID__c || ''}`}>
                      <td className="fac-geo-name">
                        <div className="fac-geo-name-main">{f.name || '(unnamed)'}</div>
                        <div className="fac-geo-name-sub">
                          VE ID{' '}
                          {f.vitalEngine_Facility_ID__c
                            ? Math.round(parseFloat(String(f.vitalEngine_Facility_ID__c)))
                            : '—'}
                        </div>
                      </td>
                      <td className="fac-geo-addr">
                        {addr || <span className="fac-geo-muted">(no address)</span>}
                      </td>
                      <td>{f.billingState || <span className="fac-geo-muted">—</span>}</td>
                      <td className="num">
                        {f.billingLatitude != null
                          ? Number(f.billingLatitude).toFixed(5)
                          : <span className="fac-geo-muted">—</span>}
                      </td>
                      <td className="num">
                        {f.billingLongitude != null
                          ? Number(f.billingLongitude).toFixed(5)
                          : <span className="fac-geo-muted">—</span>}
                      </td>
                      <td>
                        <span className={`fac-geo-badge fac-geo-badge-${bucket}`}>
                          {(QUALITY_BUCKETS.find(b => b.id === bucket) || {}).label || '—'}
                        </span>
                      </td>
                      <td className="fac-geo-links">
                        {hasCoords ? (
                          <a
                            href={coordsMapUrl(f.billingLatitude, f.billingLongitude)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Coords ↗
                          </a>
                        ) : (
                          <span className="fac-geo-muted">Coords —</span>
                        )}
                        {addr ? (
                          <a
                            href={addressSearchUrl(addr)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Address ↗
                          </a>
                        ) : (
                          <span className="fac-geo-muted">Address —</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {lastPage > 1 && (
            <div className="fac-geo-pager">
              <button onClick={() => setPage(1)} disabled={page <= 1}>« First</button>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>‹ Prev</button>
              <span>Page {page} of {lastPage}</span>
              <button onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}>Next ›</button>
              <button onClick={() => setPage(lastPage)} disabled={page >= lastPage}>Last »</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default FacilityGeocoding;
